/**
 * Bevel surface generation for Bezier curves.
 *
 * Takes a CurveData and produces the standard PrimitiveGeometry output
 * (vertices, indices, normals, topology, uvs) that the rest of the app
 * expects. The generated mesh is a tube/pipe created by sweeping a
 * circular cross-section along the evaluated curve.
 */

import { evaluateCurve } from "./curvemath.ts";
import { recomputeLoopNormals } from "./meshOps.ts";
import type { PrimitiveGeometry } from "./primitives.ts";
import {
  buildEdgesAndCornerEdges,
  triangulateTopologyForRendering,
} from "./topology.ts";
import type { CurveData, PolygonTopology } from "./types.ts";
import {
  asLoopNormals,
  EDGE_WIRE,
  EMPTY_RENDER_TRIANGLE_INDICES,
} from "./types.ts";

const TAU = 2 * Math.PI;

/**
 * Options for tuning geometry generation from outside the `modeling/` layer.
 *
 * `taperScale` — per-sample radius multipliers resolved against a taper
 * object. Must have length `positions.length` (see `evaluateCurve`). When
 * omitted the curve uses its per-point `radius` field alone. Scene-graph
 * resolution lives in the store; this module stays pure.
 */
export interface GenerateCurveGeometryOptions {
  taperScale?: Float32Array | null;
}

/**
 * Generate tube mesh geometry from curve data.
 *
 * Returns the same { vertices, indices, normals, topology, uvs } shape
 * as generatePrimitive, ready to be stored on a SceneObject.
 */
export function generateCurveGeometry(
  curve: CurveData,
  opts?: GenerateCurveGeometryOptions,
): PrimitiveGeometry {
  const { bevel, cyclic } = curve;
  const evaluation = evaluateCurve(curve);
  const { positions, frames, radii } = evaluation;
  const sampleCount = positions.length;

  if (sampleCount < 2 || !bevel.enabled || bevel.depth <= 0) {
    // No bevel — return minimal geometry (single degenerate triangle)
    return emptyGeometry();
  }

  // Validate taperScale length — if it doesn't match, ignore it rather
  // than stamp a smaller array and read past the end.
  const taperScale =
    opts?.taperScale && opts.taperScale.length === sampleCount
      ? opts.taperScale
      : null;

  const profileRes = bevel.resolution;
  // For the profile ring, we need profileRes+1 vertices to close the UV seam
  const profileStride = profileRes + 1;
  const bodyVertCount = sampleCount * profileStride;

  // Cap vertex counts (fan center + ring)
  const hasCaps = bevel.fillCaps && !cyclic;
  const capVertCount = hasCaps ? 2 * (1 + profileRes) : 0;
  const totalVertCount = bodyVertCount + capVertCount;

  const verts = new Float32Array(totalVertCount * 3);
  const vertexUVData = new Float32Array(totalVertCount * 2);

  // --- Compute arc lengths for V parameterization ---
  const arcLengths = new Float32Array(sampleCount);
  arcLengths[0] = 0;
  for (let i = 1; i < sampleCount; i++) {
    const p0 = positions[i - 1];
    const p1 = positions[i];
    const dx = p1[0] - p0[0];
    const dy = p1[1] - p0[1];
    const dz = p1[2] - p0[2];
    arcLengths[i] = arcLengths[i - 1] + Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
  const totalArcLen = arcLengths[sampleCount - 1] || 1;

  // --- Stamp circle profiles ---
  stampCurveBodyPositions(
    verts,
    sampleCount,
    profileRes,
    profileStride,
    bevel.depth,
    frames,
    radii,
    taperScale,
  );
  for (let i = 0; i < sampleCount; i++) {
    const v = arcLengths[i] / totalArcLen;
    for (let j = 0; j <= profileRes; j++) {
      const vi2 = (i * profileStride + j) * 2;
      vertexUVData[vi2] = j / profileRes;
      vertexUVData[vi2 + 1] = v;
    }
  }

  // --- Build quad topology for the tube body ---
  const rows = cyclic ? sampleCount : sampleCount - 1;
  const topology = buildCyclicQuadTopology(
    rows,
    profileRes,
    profileStride,
    cyclic,
  );

  // --- End caps ---
  if (hasCaps) {
    appendCaps(
      verts,
      vertexUVData,
      bodyVertCount,
      positions,
      frames,
      radii,
      bevel.depth,
      profileRes,
      topology,
      taperScale,
    );
  }

  const indices = triangulateTopologyForRendering(topology);
  const loopNormals = recomputeLoopNormals(verts, topology);
  const uvs = expandVertexUVsToLoopUVs(vertexUVData, topology);

  return {
    vertices: verts,
    indices,
    loopNormals,
    topology,
    uvs,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build quad topology for the tube body.
 *
 * For open curves: rows = sampleCount - 1, no row wrapping.
 * For cyclic curves: rows = sampleCount, last row wraps to first.
 * Column wrapping always happens (circular profile).
 */
function buildCyclicQuadTopology(
  rows: number,
  cols: number,
  stride: number,
  cyclic: boolean,
): PolygonTopology {
  const faceCount = rows * cols;
  const offsets = new Uint32Array(faceCount + 1);
  const corners = new Uint32Array(faceCount * 4);

  for (let i = 0; i < faceCount; i++) offsets[i] = i * 4;
  offsets[faceCount] = faceCount * 4;

  let ci = 0;
  for (let row = 0; row < rows; row++) {
    const rowBase = row * stride;
    const nextRow = cyclic && row === rows - 1 ? 0 : (row + 1) * stride;

    for (let col = 0; col < cols; col++) {
      const a = rowBase + col;
      const b = nextRow + col;
      corners[ci++] = a;
      corners[ci++] = b;
      corners[ci++] = b + 1;
      corners[ci++] = a + 1;
    }
  }

  return { offsets, corners, ...buildEdgesAndCornerEdges(offsets, corners) };
}

/**
 * Append start and end cap faces to the topology.
 * Caps are fan-triangulated ngon faces.
 */
function appendCaps(
  verts: Float32Array,
  vertexUVData: Float32Array,
  bodyVertOffset: number,
  positions: [number, number, number][],
  frames: {
    position: [number, number, number];
    normal: [number, number, number];
    binormal: [number, number, number];
  }[],
  radii: number[],
  depth: number,
  profileRes: number,
  topology: PolygonTopology,
  taperScale: Float32Array | null,
): void {
  const startCapCenter = bodyVertOffset;
  const startCapRing = startCapCenter + 1;
  const endCapCenter = startCapCenter + 1 + profileRes;
  const endCapRing = endCapCenter + 1;

  stampCurveCapPositions(
    verts,
    bodyVertOffset,
    profileRes,
    depth,
    positions,
    frames,
    radii,
    taperScale,
  );

  // Start cap UVs
  const sv2 = startCapCenter * 2;
  vertexUVData[sv2] = 0.5;
  vertexUVData[sv2 + 1] = 0;
  for (let j = 0; j < profileRes; j++) {
    const angle = (TAU * j) / profileRes;
    const vi2 = (startCapRing + j) * 2;
    vertexUVData[vi2] = 0.5 + 0.5 * Math.cos(angle);
    vertexUVData[vi2 + 1] = 0.5 + 0.5 * Math.sin(angle);
  }

  // End cap UVs
  const ev2 = endCapCenter * 2;
  vertexUVData[ev2] = 0.5;
  vertexUVData[ev2 + 1] = 1;
  for (let j = 0; j < profileRes; j++) {
    const angle = (TAU * j) / profileRes;
    const vi2 = (endCapRing + j) * 2;
    vertexUVData[vi2] = 0.5 + 0.5 * Math.cos(angle);
    vertexUVData[vi2 + 1] = 0.5 + 0.5 * Math.sin(angle);
  }

  // Build cap faces as fan-triangulated ngons appended to existing topology
  const existingFaces = topology.offsets.length - 1;
  const newFaceCount = existingFaces + 2 * profileRes;
  const newOffsets = new Uint32Array(newFaceCount + 1);
  const newCorners = new Uint32Array(
    topology.corners.length + 2 * profileRes * 3,
  );

  // Copy existing topology
  newOffsets.set(topology.offsets);
  newCorners.set(topology.corners);

  let ci = topology.corners.length;
  let fi = existingFaces;

  // Start cap triangles (reversed winding for inward-facing cap)
  for (let j = 0; j < profileRes; j++) {
    newOffsets[fi] = ci;
    const j1 = (j + 1) % profileRes;
    newCorners[ci++] = startCapCenter;
    newCorners[ci++] = startCapRing + j1;
    newCorners[ci++] = startCapRing + j;
    fi++;
  }

  // End cap triangles
  for (let j = 0; j < profileRes; j++) {
    newOffsets[fi] = ci;
    const j1 = (j + 1) % profileRes;
    newCorners[ci++] = endCapCenter;
    newCorners[ci++] = endCapRing + j;
    newCorners[ci++] = endCapRing + j1;
    fi++;
  }

  newOffsets[fi] = ci;

  // Rebuild edges for the full topology (body + caps). The edges computed by
  // buildCyclicQuadTopology only cover body corners; without this rebuild,
  // cornerEdges is shorter than corners and the WASM recompute_loop_normals
  // call hits an unreachable branch on the mismatched lengths.
  const { edges, cornerEdges, edgeFlags } = buildEdgesAndCornerEdges(
    newOffsets,
    newCorners,
  );

  // Mutate topology in place (it was just created, so safe)
  (topology as { offsets: Uint32Array }).offsets = newOffsets;
  (topology as { corners: Uint32Array }).corners = newCorners;
  (topology as { edges: Uint32Array }).edges = edges;
  (topology as { cornerEdges: Uint32Array }).cornerEdges = cornerEdges;
  (topology as { edgeFlags: Uint8Array }).edgeFlags = edgeFlags;
}

function expandVertexUVsToLoopUVs(
  vertexUVData: Float32Array,
  topology: PolygonTopology,
): Float32Array {
  const { corners } = topology;
  const loopUVs = new Float32Array(corners.length * 2);
  for (let c = 0; c < corners.length; c++) {
    const vi = corners[c] * 2;
    const ci = c * 2;
    loopUVs[ci] = vertexUVData[vi];
    loopUVs[ci + 1] = vertexUVData[vi + 1];
  }
  return loopUVs;
}

/**
 * Generate a polyline mesh from curve data — vertices along the evaluated
 * spline connected by wire edges. Used when converting a bevel-disabled
 * curve to a mesh; mirrors the wire-edge convention used by `generateCircle`
 * and `generateSkinArmature` (no faces, edges flagged EDGE_WIRE).
 */
export function generateCurvePolylineGeometry(
  curve: CurveData,
): PrimitiveGeometry {
  const { positions } = evaluateCurve(curve);
  const sampleCount = positions.length;
  if (sampleCount < 2) return emptyGeometry();

  const vertices = new Float32Array(sampleCount * 3);
  for (let i = 0; i < sampleCount; i++) {
    vertices[i * 3] = positions[i][0];
    vertices[i * 3 + 1] = positions[i][1];
    vertices[i * 3 + 2] = positions[i][2];
  }

  const segCount = curve.cyclic ? sampleCount : sampleCount - 1;
  const edges = new Uint32Array(segCount * 2);
  for (let i = 0; i < segCount; i++) {
    const a = i;
    const b = (i + 1) % sampleCount;
    edges[i * 2] = Math.min(a, b);
    edges[i * 2 + 1] = Math.max(a, b);
  }
  const edgeFlags = new Uint8Array(segCount).fill(EDGE_WIRE);

  const topology: PolygonTopology = {
    offsets: new Uint32Array([0]),
    corners: new Uint32Array(0),
    edges,
    cornerEdges: new Uint32Array(0),
    edgeFlags,
  };

  return {
    vertices,
    indices: EMPTY_RENDER_TRIANGLE_INDICES,
    loopNormals: asLoopNormals(new Float32Array(0)),
    topology,
    uvs: new Float32Array(0),
  };
}

/** Return empty geometry for curves with no bevel. */
function emptyGeometry(): PrimitiveGeometry {
  return {
    vertices: new Float32Array(0),
    indices: EMPTY_RENDER_TRIANGLE_INDICES,
    loopNormals: asLoopNormals(new Float32Array(0)),
    topology: {
      offsets: new Uint32Array([0]),
      corners: new Uint32Array(0),
      edges: new Uint32Array(0),
      cornerEdges: new Uint32Array(0),
      edgeFlags: new Uint8Array(0),
    },
    uvs: new Float32Array(0),
  };
}

type CurveFrame = {
  position: [number, number, number];
  normal: [number, number, number];
  binormal: [number, number, number];
};

function stampCurveBodyPositions(
  verts: Float32Array,
  sampleCount: number,
  profileRes: number,
  profileStride: number,
  bevelDepth: number,
  frames: CurveFrame[],
  radii: number[],
  taperScale: Float32Array | null,
): void {
  for (let i = 0; i < sampleCount; i++) {
    const frame = frames[i];
    const r = bevelDepth * radii[i] * (taperScale ? taperScale[i] : 1);
    for (let j = 0; j <= profileRes; j++) {
      const angle = (TAU * j) / profileRes;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const vi3 = (i * profileStride + j) * 3;
      verts[vi3] =
        frame.position[0] +
        (cos * frame.normal[0] + sin * frame.binormal[0]) * r;
      verts[vi3 + 1] =
        frame.position[1] +
        (cos * frame.normal[1] + sin * frame.binormal[1]) * r;
      verts[vi3 + 2] =
        frame.position[2] +
        (cos * frame.normal[2] + sin * frame.binormal[2]) * r;
    }
  }
}

function stampCurveCapPositions(
  verts: Float32Array,
  bodyVertOffset: number,
  profileRes: number,
  bevelDepth: number,
  positions: [number, number, number][],
  frames: CurveFrame[],
  radii: number[],
  taperScale: Float32Array | null,
): void {
  const startCapCenter = bodyVertOffset;
  const startCapRing = startCapCenter + 1;
  const endCapCenter = startCapCenter + 1 + profileRes;
  const endCapRing = endCapCenter + 1;
  const last = positions.length - 1;

  const sp = positions[0];
  const sv3 = startCapCenter * 3;
  verts[sv3] = sp[0];
  verts[sv3 + 1] = sp[1];
  verts[sv3 + 2] = sp[2];

  const sFrame = frames[0];
  const sr = bevelDepth * radii[0] * (taperScale ? taperScale[0] : 1);
  for (let j = 0; j < profileRes; j++) {
    const angle = (TAU * j) / profileRes;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const vi3 = (startCapRing + j) * 3;
    verts[vi3] =
      sp[0] + (cos * sFrame.normal[0] + sin * sFrame.binormal[0]) * sr;
    verts[vi3 + 1] =
      sp[1] + (cos * sFrame.normal[1] + sin * sFrame.binormal[1]) * sr;
    verts[vi3 + 2] =
      sp[2] + (cos * sFrame.normal[2] + sin * sFrame.binormal[2]) * sr;
  }

  const ep = positions[last];
  const ev3 = endCapCenter * 3;
  verts[ev3] = ep[0];
  verts[ev3 + 1] = ep[1];
  verts[ev3 + 2] = ep[2];

  const eFrame = frames[last];
  const er = bevelDepth * radii[last] * (taperScale ? taperScale[last] : 1);
  for (let j = 0; j < profileRes; j++) {
    const angle = (TAU * j) / profileRes;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const vi3 = (endCapRing + j) * 3;
    verts[vi3] =
      ep[0] + (cos * eFrame.normal[0] + sin * eFrame.binormal[0]) * er;
    verts[vi3 + 1] =
      ep[1] + (cos * eFrame.normal[1] + sin * eFrame.binormal[1]) * er;
    verts[vi3 + 2] =
      ep[2] + (cos * eFrame.normal[2] + sin * eFrame.binormal[2]) * er;
  }
}

/**
 * Fast-path position-only update of curve tube geometry during drag.
 *
 * Rewrites only the vertex positions (body + caps) into a new Float32Array
 * sized to match the existing buffer. Topology, indices, loopNormals, and
 * UVs are left untouched — the caller keeps the old references. This lets
 * live drag skip triangulation / normal recomputation / edge rebuild.
 *
 * Returns null if the expected vertex count doesn't match `existing` —
 * caller should fall back to `generateCurveGeometry`. This shouldn't
 * happen during a drag (bevel.resolution / cyclic / fillCaps don't change
 * mid-command) but guards against surprise.
 */
export function updateCurveGeometryPositions(
  curve: CurveData,
  existing: Float32Array,
  opts?: GenerateCurveGeometryOptions,
): Float32Array | null {
  const { bevel, cyclic } = curve;
  if (existing.length === 0 || !bevel.enabled || bevel.depth <= 0) return null;

  const evaluation = evaluateCurve(curve);
  const { positions, frames, radii } = evaluation;
  const sampleCount = positions.length;
  if (sampleCount < 2) return null;

  const profileRes = bevel.resolution;
  const profileStride = profileRes + 1;
  const bodyVertCount = sampleCount * profileStride;
  const hasCaps = bevel.fillCaps && !cyclic;
  const capVertCount = hasCaps ? 2 * (1 + profileRes) : 0;
  const totalVertCount = bodyVertCount + capVertCount;

  if (totalVertCount * 3 !== existing.length) return null;

  const taperScale =
    opts?.taperScale && opts.taperScale.length === sampleCount
      ? opts.taperScale
      : null;

  const verts = new Float32Array(existing.length);
  stampCurveBodyPositions(
    verts,
    sampleCount,
    profileRes,
    profileStride,
    bevel.depth,
    frames,
    radii,
    taperScale,
  );
  if (hasCaps) {
    stampCurveCapPositions(
      verts,
      bodyVertCount,
      profileRes,
      bevel.depth,
      positions,
      frames,
      radii,
      taperScale,
    );
  }
  return verts;
}
